'use client';

import { CodeBlock, CodeBlockCopyButton } from '@/components/ai-elements/code-block';
import { MessageResponse } from '@/components/ai-elements/message';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import {
  Code2,
  Download,
  Eye,
  History as HistoryIcon,
  ExternalLink,
  FilePen,
  FileWarning,
  Minus,
  Plus,
  RotateCw,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getFileContent } from './api';
import { FileEditor } from './file-editor';
import {
  categorizePreview,
  guessCsvDelimiter,
  parseCsv,
  shikiLanguage,
  type PreviewKind,
} from './mime';
import { formatBytes, formatTimestamp } from './tree';
import type { FileContentResponse, WorkspaceFileEntry } from './types';

const TEXT_KINDS = new Set<PreviewKind>(['code', 'markdown', 'html', 'csv']);

interface FilePreviewProps {
  workspaceId: string;
  file: WorkspaceFileEntry;
  /** When set, preview a specific historical version instead of latest. */
  version?: number;
  onOpenHistory?: () => void;
}

type Phase = 'loading' | 'ready' | 'error';

export function FilePreview({ workspaceId, file, version, onOpenHistory }: FilePreviewProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState<FileContentResponse | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [editing, setEditing] = useState(false);

  const kind = categorizePreview(content?.mime ?? file.mime, file.path);
  // Editing is only offered for the LATEST version of a text file — historical
  // versions are read-only (you view them, you don't fork-edit a past version).
  const isEditable = TEXT_KINDS.has(kind) && version === undefined;

  // Leaving edit mode whenever the selected file / viewed version changes.
  useEffect(() => {
    setEditing(false);
  }, [file.path, version]);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);
    setContent(null);
    setText(null);

    getFileContent(workspaceId, file.path, version).then(async (res) => {
      if (cancelled) return;
      if (!res.ok) {
        setPhase('error');
        setError(res.notAvailable ? 'This file is not available in storage yet.' : res.error);
        return;
      }
      setContent(res.data);
      const previewKind = categorizePreview(res.data.mime ?? file.mime, file.path);
      if (TEXT_KINDS.has(previewKind)) {
        // Use inlineText when the API provides it — never re-fetch the URL.
        if (typeof res.data.inlineText === 'string') {
          setText(res.data.inlineText);
          setPhase('ready');
          return;
        }
        // Otherwise fetch the presigned URL and read it as text.
        try {
          const r = await fetch(res.data.url);
          if (cancelled) return;
          if (!r.ok) throw new Error(`Could not load file contents (${r.status})`);
          const body = await r.text();
          if (cancelled) return;
          setText(body);
          setPhase('ready');
        } catch (err) {
          if (cancelled) return;
          setPhase('error');
          setError(err instanceof Error ? err.message : 'Could not load file contents');
        }
      } else {
        setPhase('ready');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [workspaceId, file.path, version, nonce]);

  const retry = useCallback(() => setNonce((n) => n + 1), []);

  const download = useCallback(() => {
    if (!content?.url) return;
    const a = document.createElement('a');
    a.href = content.url;
    a.download = file.path.split('/').pop() ?? 'download';
    a.rel = 'noopener';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [content, file.path]);

  const fileName = file.path.split('/').pop() ?? file.path;

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-preview-kind={kind}
      data-preview-phase={phase}
    >
      <PreviewHeader
        fileName={fileName}
        kind={kind}
        version={version}
        canDownload={!!content?.url}
        onDownload={download}
        onOpenExternal={content?.url ? () => window.open(content.url, '_blank', 'noopener,noreferrer') : undefined}
        onOpenHistory={onOpenHistory}
        canEdit={isEditable && phase === 'ready' && text !== null}
        editing={editing}
        onEdit={() => setEditing(true)}
      />

      <div className="min-h-0 flex-1 overflow-auto">
        {phase === 'loading' && (
          <div className="flex h-full items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
            <Spinner className="size-4" /> Loading preview…
          </div>
        )}

        {phase === 'error' && (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
            <FileWarning className="size-6 text-amber-500" />
            <p className="max-w-sm text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" size="sm" onClick={retry} className="gap-1.5">
              <RotateCw className="size-3.5" /> Retry
            </Button>
          </div>
        )}

        {phase === 'ready' && content && editing && text !== null ? (
          <FileEditor
            key={file.path}
            workspaceId={workspaceId}
            file={file}
            initialText={text}
            initialVersion={file.version}
            mime={content.mime ?? file.mime}
            onClose={() => setEditing(false)}
          />
        ) : (
          phase === 'ready' && content && (
            <PreviewBody kind={kind} file={file} content={content} text={text} onDownload={download} />
          )
        )}
      </div>
    </div>
  );
}

function PreviewHeader({
  fileName,
  kind,
  version,
  canDownload,
  onDownload,
  onOpenExternal,
  onOpenHistory,
  canEdit,
  editing,
  onEdit,
}: {
  fileName: string;
  kind: PreviewKind;
  version?: number;
  canDownload: boolean;
  onDownload: () => void;
  onOpenExternal?: () => void;
  onOpenHistory?: () => void;
  canEdit?: boolean;
  editing?: boolean;
  onEdit?: () => void;
}) {
  return (
    <div className="flex min-h-11 shrink-0 items-center gap-2 border-b border-border bg-muted/20 px-3">
      <span className="truncate text-sm font-medium" title={fileName}>
        {fileName}
      </span>
      {version !== undefined && (
        <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[11px] font-medium text-amber-500">
          v{version}
        </span>
      )}
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{kind}</span>
      <div className="ml-auto flex items-center gap-1">
        {canEdit && !editing && onEdit && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs"
            onClick={onEdit}
            data-testid="edit-file-button"
          >
            <FilePen className="size-3.5" /> Edit
          </Button>
        )}
        {onOpenHistory && (
          <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={onOpenHistory}>
            <HistoryIcon className="size-3.5" /> History
          </Button>
        )}
        {onOpenExternal && (
          <Button variant="ghost" size="icon" className="size-7" title="Open in new tab" onClick={onOpenExternal}>
            <ExternalLink className="size-3.5" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          title="Download"
          disabled={!canDownload}
          onClick={onDownload}
        >
          <Download className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}

function PreviewBody({
  kind,
  file,
  content,
  text,
  onDownload,
}: {
  kind: PreviewKind;
  file: WorkspaceFileEntry;
  content: FileContentResponse;
  text: string | null;
  onDownload: () => void;
}) {
  switch (kind) {
    case 'code':
      return <CodePreview path={file.path} text={text ?? ''} />;
    case 'markdown':
      return <MarkdownPreview text={text ?? ''} />;
    case 'html':
      return <HtmlPreview text={text ?? ''} />;
    case 'csv':
      return <CsvPreview path={file.path} mime={content.mime} text={text ?? ''} />;
    case 'pdf':
      return (
        <iframe
          title={file.path}
          src={content.url}
          className="h-full min-h-[420px] w-full border-0 bg-white"
        />
      );
    case 'image':
      return <ImagePreview url={content.url} alt={file.path} />;
    case 'video':
      // biome-ignore lint/a11y/useMediaCaption: user-uploaded media, no track available
      return (
        <div className="grid h-full place-items-center bg-black/40 p-4">
          <video src={content.url} controls className="max-h-full max-w-full rounded-md" />
        </div>
      );
    case 'audio':
      return (
        <div className="grid h-full place-items-center p-6">
          {/* biome-ignore lint/a11y/useMediaCaption: user-uploaded media, no track available */}
          <audio src={content.url} controls className="w-full max-w-md" />
        </div>
      );
    default:
      return <UnknownPreview file={file} content={content} onDownload={onDownload} />;
  }
}

function CodePreview({ path, text }: { path: string; text: string }) {
  return (
    <div className="p-3">
      <CodeBlock code={text} language={shikiLanguage(path)} showLineNumbers>
        <CodeBlockCopyButton />
      </CodeBlock>
    </div>
  );
}

function MarkdownPreview({ text }: { text: string }) {
  const [mode, setMode] = useState<'rendered' | 'raw'>('rendered');
  return (
    <div className="flex h-full flex-col">
      <Toggle
        options={[
          { value: 'rendered', label: 'Rendered', icon: <Eye className="size-3.5" /> },
          { value: 'raw', label: 'Raw', icon: <Code2 className="size-3.5" /> },
        ]}
        value={mode}
        onChange={(v) => setMode(v as 'rendered' | 'raw')}
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {mode === 'rendered' ? (
          <MessageResponse>{text}</MessageResponse>
        ) : (
          <CodeBlock code={text} language="markdown" showLineNumbers>
            <CodeBlockCopyButton />
          </CodeBlock>
        )}
      </div>
    </div>
  );
}

function HtmlPreview({ text }: { text: string }) {
  const [mode, setMode] = useState<'preview' | 'source'>('preview');
  return (
    <div className="flex h-full flex-col">
      <Toggle
        options={[
          { value: 'preview', label: 'Preview', icon: <Eye className="size-3.5" /> },
          { value: 'source', label: 'Source', icon: <Code2 className="size-3.5" /> },
        ]}
        value={mode}
        onChange={(v) => setMode(v as 'preview' | 'source')}
      />
      <div className="min-h-0 flex-1 overflow-auto">
        {mode === 'preview' ? (
          // Untrusted generated HTML. sandbox WITHOUT allow-same-origin so the
          // document cannot read cookies / same-origin storage of the app.
          <iframe
            title="HTML preview"
            sandbox="allow-scripts"
            srcDoc={text}
            className="h-full min-h-[420px] w-full border-0 bg-white"
            data-testid="html-preview-frame"
          />
        ) : (
          <div className="p-3">
            <CodeBlock code={text} language="html" showLineNumbers>
              <CodeBlockCopyButton />
            </CodeBlock>
          </div>
        )}
      </div>
    </div>
  );
}

function CsvPreview({ path, mime, text }: { path: string; mime?: string; text: string }) {
  const parsed = useMemo(
    () => parseCsv(text, guessCsvDelimiter(path, mime), 500),
    [text, path, mime],
  );
  if (parsed.rows.length === 0) {
    return <div className="p-6 text-sm text-muted-foreground">Empty file.</div>;
  }
  const [header, ...body] = parsed.rows;
  return (
    <div className="p-3">
      <div className="overflow-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {header.map((cell, i) => (
                <TableHead key={i} className="whitespace-nowrap">
                  {cell}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {body.map((r, ri) => (
              <TableRow key={ri}>
                {header.map((_, ci) => (
                  <TableCell key={ci} className="whitespace-nowrap font-mono text-xs">
                    {r[ci] ?? ''}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {parsed.truncated && (
        <p className="mt-2 text-xs text-muted-foreground">
          Showing first {body.length} rows — file has more (truncated).
        </p>
      )}
    </div>
  );
}

function ImagePreview({ url, alt }: { url: string; alt: string }) {
  const [zoom, setZoom] = useState(1);
  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-1 border-b border-border bg-muted/20 px-2 py-1">
        <Button variant="ghost" size="icon" className="size-7" onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))} title="Zoom out">
          <Minus className="size-3.5" />
        </Button>
        <span className="w-12 text-center text-xs tabular-nums text-muted-foreground">{Math.round(zoom * 100)}%</span>
        <Button variant="ghost" size="icon" className="size-7" onClick={() => setZoom((z) => Math.min(8, z + 0.25))} title="Zoom in">
          <Plus className="size-3.5" />
        </Button>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setZoom(1)}>
          Reset
        </Button>
      </div>
      <div className="grid min-h-0 flex-1 place-items-center overflow-auto bg-muted/20 p-4">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={url}
          alt={alt}
          className="origin-center transition-transform"
          style={{ transform: `scale(${zoom})` }}
        />
      </div>
    </div>
  );
}

function UnknownPreview({
  file,
  content,
  onDownload,
}: {
  file: WorkspaceFileEntry;
  content: FileContentResponse;
  onDownload: () => void;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
      <div className="space-y-1">
        <p className="text-sm font-medium">No inline preview for this file type</p>
        <p className="text-xs text-muted-foreground">
          {content.mime || 'unknown type'} · {formatBytes(content.size ?? file.size)}
        </p>
        <p className="text-xs text-muted-foreground">Updated {formatTimestamp(file.updatedAt)}</p>
      </div>
      <Button variant="outline" size="sm" className="gap-1.5" onClick={onDownload}>
        <Download className="size-3.5" /> Download
      </Button>
    </div>
  );
}

function Toggle({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string; icon?: React.ReactNode }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-1 border-b border-border bg-muted/20 px-2 py-1.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors',
            value === opt.value
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  );
}
